/**
 * Wire contract for the admin payout-cards monitoring surface (AUT-115 /
 * PHASE-B-API-4):
 *
 *   - `GET  /admin/cards`                 → `ListAdminCardsResultDto`
 *   - `POST /admin/cards/:id/force-unbind` → `AdminCardDto`
 *
 * Centralised in `@carcheck/shared` so the api + admin SPA import the same
 * source of truth — no api↔admin mirror drift.
 *
 * **Why a DISTINCT shape from `InspectorCardDto`** (architect 🟠 #2):
 * `InspectorCardDto` is the OWN-inspector contract — it exposes
 * `holderName` UNCONDITIONALLY because the inspector is looking at their
 * own card. The admin surface is CROSS-inspector: a cardholder's legal
 * name is PII (same class as the AUT-117 payouts inspector name), so this
 * shape gates `holderName` behind `view_pii` and carries `piiRevealed`
 * to tell the SPA which variant it received. It also adds `inspectorId`
 * (the owning inspector) which the own-inspector contract never needs.
 *
 * **PII discipline (architect 🔴 #1 — the defining risk):** the
 * `recurring_token`, `bindTransactionId`, `bindIntentId`, and the full PAN
 * NEVER cross the wire on ANY admin card surface — only the masked PAN
 * (`last4`), `brand`, `status`, `isPrimary`, `inspectorId`, timestamps
 * (always exposed — operational), and the `view_pii`-gated `holderName`.
 * The api-side mapper (`to-admin-card-dto.ts`) hand-projects these fields
 * off the Prisma row (never spreads it) and a serialization test asserts
 * none of the secret columns can leak.
 *
 * Date fields are ISO-8601 strings (JSON has no `Date`); consumers convert
 * at the render boundary via `new Date(dto.createdAt)`.
 */
import { INSPECTOR_CARD_STATUS_VALUES } from './inspector-card.js';
/**
 * Admin-facing card status filter — the FULL 7-value `InspectorCardStatus`
 * surface (architect 🟠 #3). Unlike the own-inspector list (which hides
 * `abandoned`), admin reconciliation needs every state visible:
 * `binding | active | expired | unbound | abandoned | suspended | failed`.
 *
 * Derived from the shared `INSPECTOR_CARD_STATUS_VALUES` const so it can
 * NEVER drift from the Prisma enum — the api filter Zod schema imports the
 * SAME source rather than hand-typing the values. The ticket's original
 * `bound/unbound/suspended` triple was WRONG — there is no `bound` status
 * (the bound state is `active`); a `?status=bound` query is rejected 400.
 */
export const ADMIN_CARD_STATUSES = INSPECTOR_CARD_STATUS_VALUES;
//# sourceMappingURL=admin-card.js.map